import type { ZohoContact } from '@deltasuite/zoho-client';
import { useEffect, useState } from 'react';
import { getSiblings } from '../../api/siblings';

interface Props {
  primary: ZohoContact;
  onConfirm: (input: { notes: string; dropSiblings: readonly string[] }) => void;
  onCancel: () => void;
}

// R3a Database Update flow: when the active contact has moved on, this
// panel slides in showing up to 6 other contacts at the same company. The
// rep ticks any siblings that should also flag Dropped=true. Confirm
// dispatches a `database_update` disposition to the runner — the worker
// handles the primary contact_update + sibling fan-out atomically.
//
// Per reframe Q2: siblings are live-fetched via the Zoho proxy on open.
// No local cache.
export function DatabaseUpdatePanel({ primary, onConfirm, onCancel }: Props) {
  const [siblings, setSiblings] = useState<readonly ZohoContact[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dropIds, setDropIds] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const companyId = primary.Account_Name?.id;
    if (!companyId) {
      setSiblings([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void getSiblings(companyId, primary.id, 6)
      .then((res) => {
        if (cancelled) return;
        setSiblings(res.data);
      })
      .catch(() => {
        if (cancelled) return;
        setSiblings([]);
        setLoadError("Couldn't fetch sibling contacts. You can still mark this contact dropped.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [primary.id, primary.Account_Name?.id]);

  const toggle = (id: string) => {
    setDropIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onSubmit = () => {
    onConfirm({ notes: notes.trim(), dropSiblings: [...dropIds] });
  };

  return (
    <aside
      role="dialog"
      aria-modal="true"
      aria-label="Database update"
      className="fixed inset-y-0 right-0 z-30 flex w-full max-w-md flex-col border-l border-graphite-100 bg-surface shadow-xl"
    >
      <header className="flex items-baseline justify-between border-b border-graphite-100 px-6 py-4">
        <div>
          <h2 className="font-sans text-lg font-semibold text-graphite-900">Database Update</h2>
          <p className="mt-0.5 font-mono text-xs text-graphite-500">
            Mark contacts that have moved on at this company.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="font-mono text-xs uppercase tracking-wider text-graphite-500 hover:text-graphite-900"
        >
          Cancel
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        <section>
          <h3 className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-graphite-500">
            Primary
          </h3>
          <p className="mt-1 font-sans text-sm text-graphite-900">
            {[primary.First_Name, primary.Last_Name].filter(Boolean).join(' ')}
            <span className="ml-2 font-mono text-xs text-signal-amber">will be dropped</span>
          </p>
          {primary.Account_Name?.name ? (
            <p className="mt-0.5 font-mono text-xs text-graphite-500">
              {primary.Account_Name.name}
            </p>
          ) : null}
        </section>

        <section className="mt-6">
          <h3 className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-graphite-500">
            Other contacts at this company
          </h3>
          {loading ? (
            <p className="mt-2 font-mono text-xs text-graphite-500">Loading…</p>
          ) : siblings && siblings.length === 0 ? (
            <p className="mt-2 font-mono text-xs text-graphite-500">
              No other contacts on file at this company.
            </p>
          ) : (
            <ul className="mt-3 space-y-2">
              {(siblings ?? []).map((s) => {
                const checked = dropIds.has(s.id);
                return (
                  <li
                    key={s.id}
                    className="rounded-md border border-graphite-100 bg-surface-sunk p-3"
                  >
                    <label className="flex cursor-pointer items-start gap-3">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(s.id)}
                        className="mt-1 size-4 accent-signal-amber"
                      />
                      <div className="flex-1">
                        <p className="font-sans text-sm text-graphite-900">
                          {[s.First_Name, s.Last_Name].filter(Boolean).join(' ') || s.id}
                          {s.Title ? (
                            <span className="ml-2 font-mono text-xs text-graphite-500">
                              {s.Title}
                            </span>
                          ) : null}
                        </p>
                        {s.Email ? (
                          <p className="font-mono text-xs text-graphite-500">{s.Email}</p>
                        ) : null}
                      </div>
                      <span
                        className={
                          'font-mono text-[0.65rem] uppercase tracking-wider ' +
                          (checked ? 'text-signal-amber' : 'text-graphite-500')
                        }
                      >
                        {checked ? 'drop' : 'keep'}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          {loadError ? (
            <p role="alert" className="mt-2 font-mono text-xs text-signal-amber">
              {loadError}
            </p>
          ) : null}
        </section>

        <section className="mt-6">
          <h3 className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-graphite-500">
            Notes (optional)
          </h3>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Anything worth recording on the audit row…"
            className="mt-2 w-full rounded-md border border-graphite-300 bg-surface px-3 py-2 font-sans text-sm text-graphite-900"
          />
        </section>
      </div>

      <footer className="border-t border-graphite-100 px-6 py-4">
        <div className="flex items-center justify-between">
          <p className="font-mono text-xs text-graphite-500">
            +3 pts · {dropIds.size} sibling{dropIds.size === 1 ? '' : 's'} flagged
          </p>
          <button
            type="button"
            onClick={onSubmit}
            className="rounded-md bg-graphite-900 px-4 py-2 font-sans text-sm font-medium text-surface hover:bg-graphite-700"
          >
            Confirm
          </button>
        </div>
      </footer>
    </aside>
  );
}
